import React from "react"
import { Box } from "@chakra-ui/react"
import { Swiper, SwiperSlide } from "swiper/react"

import { SectionProps, withSection } from "@app/hoc/Section"
import useCarousel from "@hooks/useCarousel"
import useCarouselImages from "@hooks/useCarouselImages"
import CarouselNavigation from "@components/CarouselNavigation/CarouselNavigation"
import HeroSlideMobile from "./HeroSlideMobile"
import HeroSlideDesktop from "./HeroSlideDesktop"

import "swiper/css"
import "swiper/css/pagination"

export interface HeroSlideProps {
  slide: any
  handleTrackingClick: VoidFunction
}

export interface HeroProps extends SectionProps {
  carousel?: GatsbyTypes.SanityCarousel
}

const Hero = ({ carousel, innerRef, handleTrackingClick }: HeroProps) => {
  const slides = useCarouselImages(carousel, false)

  const { settings, prevSlideRef, nextSlideRef } = useCarousel(carousel?.settings, {
    slidesPerView: slides?.length > 1 ? 1.3 : 1,
    spaceBetween: 10,
    preloadImages: false,
    centeredSlides: true,
    loop: slides?.length > 1 ? true : false,
    initialSlide: 0,
  })

  const {
    settings: settingsMobile,
    prevSlideRef: prevSlideRefMobile,
    nextSlideRef: nextSlideRefMobile,
  } = useCarousel(carousel?.settings, {
    slidesPerView: 1,
    loop: slides?.length > 1 ? true : false,
    spaceBetween: 0,
    preloadImages: false,
    centeredSlides: true,
    initialSlide: 0,
  })

  const width = React.useMemo(() => {
    return 1 / settings.slidesPerView
  }, [settings])

  const transformWidth = React.useMemo(() => {
    if (!settings.loop) return 0
    const loopSlides = Math.ceil(settings.slidesPerView)
    const widthLooped = width * loopSlides
    let ret
    if (settings.centeredSlides) {
      const centerOffset = (1 - width) / 2
      ret = widthLooped - centerOffset
    } else {
      ret = widthLooped
    }
    return `calc(${ret * -100}% - ${loopSlides * settings.spaceBetween}px)`
  }, [settings, width])

  const widthMobile = React.useMemo(() => {
    return 1 / settingsMobile.slidesPerView
  }, [settingsMobile])

  const transformWidthMobile = React.useMemo(() => {
    if (!settingsMobile.loop) return 0
    const loopSlides = Math.ceil(settingsMobile.slidesPerView)
    const widthLooped = widthMobile * loopSlides
    let ret
    if (settingsMobile.centeredSlides) {
      const centerOffset = (1 - widthMobile) / 2
      ret = widthLooped - centerOffset
    } else {
      ret = widthLooped
    }
    return `calc(${ret * -100}% - ${loopSlides * settingsMobile.spaceBetween}px)`
  }, [settingsMobile, widthMobile])

  return (
    <>
      <Box
        display={["none", "block"]}
        position="relative"
        ref={innerRef}
        sx={{
          ".swiper-wrapper": {
            transform: `translate3d(${transformWidth}, 0px, 0px)`,
          },
          ".swiper-slide": {
            marginRight: settings.spaceBetween + "px",
            width: width * 100 + "%",
          },
          img: {
            // maxHeight: slides?.length > 1 ? "unset" : "558px",
            // height: slides?.length > 1 ? "unset" : "558px",
            width: slides?.length > 1 ? "unset" : "100%",
            objectFit:
              slides?.length > 1 ? "cover" : typeof window !== "undefined" ? (window.innerWidth > 1440 ? "fill" : "contain") : "contain",
          },
        }}
      >
        <Swiper {...settings}>
          {slides?.map(slide => {
            const props: HeroSlideProps = { slide, handleTrackingClick }
            return (
              <SwiperSlide key={slide?.key}>
                <HeroSlideDesktop {...props} />
              </SwiperSlide>
            )
          })}
          {slides?.length > 1 ? (
            <>
              <CarouselNavigation ref={prevSlideRef} color="text.icons" direction="prev" />
              <CarouselNavigation ref={nextSlideRef} color="text.icons" direction="next" />
            </>
          ) : null}
        </Swiper>
      </Box>
      <Box
        display={["block", "none"]}
        position="relative"
        ref={innerRef}
        sx={{
          ".swiper-wrapper": {
            transform: `translate3d(${transformWidthMobile}, 0px, 0px)`,
          },
          ".swiper-slide": {
            marginRight: settingsMobile.spaceBetween + "px",
            width: widthMobile * 100 + "%",
          },
        }}
      >
        <Swiper {...settingsMobile}>
          {slides?.map(slide => {
            const props: HeroSlideProps = { slide, handleTrackingClick }
            return (
              <SwiperSlide key={slide?.key}>
                <HeroSlideMobile {...props} />
              </SwiperSlide>
            )
          })}

          {slides?.length > 1 ? (
            <>
              <CarouselNavigation ref={prevSlideRefMobile} color="text.icons" direction="prev" />
              <CarouselNavigation ref={nextSlideRefMobile} color="text.icons" direction="next" />
            </>
          ) : null}
        </Swiper>
      </Box>
    </>
  )
}

export default React.memo(withSection(Hero))
